<script setup lang="ts">
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  PinInput,
  PinInputGroup,
  PinInputSeparator,
  PinInputInput,
} from '@/components/ui/pin-input'
import { ref, watch, defineEmits } from 'vue'
import { Button } from '@/components/ui/button'
import LoginCard from '@/views/auth/components/LoginCard.vue'
import { ArrowLeft, Loader2 } from 'lucide-vue-next'
import { AuthApi } from '@/services/api/auth.ts'
import { AuthService } from '@/services/auth.ts'
import { CurrentUserService } from '@/services/current-user'
import { DashboardFilters } from '@/services/dashboard-filters.ts'

const emit = defineEmits<{
  (event: 'success'): void
  (event: 'back'): void
}>()

const props = defineProps<{
  temp_auth_token: string
  otp_use_email: boolean
}>()

const isLoading = ref<boolean>(false)
const pin = ref<string[]>([])
const errorMessage = ref<string | null>(null)
const timeout = ref<number>(60)

const onSubmit = async () => {
  if (pin.value.length < 6) {
    errorMessage.value = 'OTP must be 6 digits long'
    return
  }

  try {
    isLoading.value = true
    const tokens = await AuthApi.validateOtp({
      otp: pin.value.join(''),
      temp_auth_token: props.temp_auth_token,
    })
    AuthService.setTokens(tokens.access, tokens.refresh)
    CurrentUserService.setCurrentUser(tokens.user)
    // Use the new method that ensures currentRole is always updated
    await DashboardFilters.initializeRolesAfterLogin(tokens.roles)
    isLoading.value = false
    emit('success')
  } catch (error) {
    // TODO: display error on the UI
    console.error('Error:', error)
  }
  isLoading.value = false
}

watch(pin, (newValue) => {
  if (newValue.length === 6) {
    onSubmit()
  }
})

function resendOtpCode() {
  AuthApi.resendOtpCode({temp_auth_token: props.temp_auth_token})
  timeout.value = 60
  startTimeout()
}

// Start the timeout
const startTimeout = () => {
  const interval = setInterval(() => {
    if (timeout.value > 0) {
      timeout.value -= 1
    } else {
      clearInterval(interval)
    }
  }, 1000)
}

startTimeout()
</script>

<template>
  <LoginCard>
    <CardHeader>
      <img
        src="/src/assets/media/revio/revio.svg"
        class="shrink-0 h-6 text-center p-0 m-0 mb-2.5"
        alt="Revio"
      />
      <CardTitle v-if="otp_use_email" class="text-center">
        Verify your email
      </CardTitle>
      <CardTitle v-else class="text-center">
        Authenticator Verification
      </CardTitle>
      <CardDescription v-if="otp_use_email" class="text-center"
        >Enter the verification code we sent to your email
      </CardDescription>
      <CardDescription v-else class="text-center"
        >Enter the verification code from your authenticator app
      </CardDescription>

      <img
        v-if="otp_use_email"
        src="/src/assets/media/illustrations/email.svg"
        class="h-24 mt-4"
        alt=""
      />
    </CardHeader>

    <PinInput
      id="pin-input"
      v-model="pin"
      placeholder=""
      @complete="onSubmit"
      class="flex justify-center"
    >
      <PinInputGroup>
        <template v-for="(id, index) in 6" :key="id">
          <PinInputInput
            class="border h-11 w-9 m-1 text-base font-bold"
            style="border-radius: 8px"
            :index="index"
          />
          <template v-if="index === 2">
            <PinInputSeparator />
          </template>
        </template>
      </PinInputGroup>
    </PinInput>

    <div v-if="errorMessage" class="text-red-500 text-center mt-2">{{ errorMessage }}</div>

    <div v-if="otp_use_email" class="flex items-center justify-center mt-4 mb-4">
      <span class="text-sm text-gray-700">
        Didn’t receive a code?
      </span>
      <span v-if="timeout > 0" class="text-sm text-gray-500 ml-2">
        Wait {{ timeout }}s to retry
      </span>
      <Button v-if="timeout === 0" variant="ghost" class="text-sm" @click="resendOtpCode">
        Resend
      </Button>
    </div>

    <Button class="mt-4 w-full" @click="onSubmit" :disabled="isLoading">
      <Loader2 v-if="isLoading" class="size-4 mr-2 animate-spin" />
      Sign In
    </Button>
    <Button variant="ghost" class="mt-4 w-full text-gray-600" @click.prevent="emit('back')">
      <ArrowLeft />
      Back to Login
    </Button>
  </LoginCard>
</template>

<style scoped></style>
